/**
 * Rename (v0.2) — renames a symbol across all files.
 *
 * Validation:
 *   - new name cannot be a reserved keyword
 *   - new name cannot collide with an existing top-level symbol of the
 *     same kind in the workspace
 *   - new name must be a valid identifier (letter, digit, underscore)
 *
 * If validation fails, the request returns null and the editor shows
 * the failure as "Cannot rename" without applying any edits.
 *
 * Edit strategy:
 *   - Edit the declaration's name span
 *   - Edit each recorded reference span — replace only the substring
 *     matching the old name within the larger reference span (so a
 *     reference like `Orders.CreateOrder` only changes `Orders` when
 *     renaming the module).
 */

import type { TextEdit, WorkspaceEdit } from "vscode-languageserver";
import type { CursorContext } from "../workspace/cursor-context.js";
import type { ReferenceIndex } from "../workspace/reference-index.js";
import type { SymbolEntry, SymbolTable } from "../workspace/symbol-table.js";
import type { Workspace } from "../workspace/workspace.js";
import { resolveSymbolAtCursor } from "../workspace/resolve.js";
import { toLspRange } from "../lsp-types.js";
import { RESERVED_KEYWORDS, tokenize } from "@archlang/engine";

// The authoritative lexer keyword set (incl. every v0.8 process keyword —
// `select`/`go`/`as`/`reversible`/… — which a hand-maintained list kept missing,
// letting a rename produce unparseable source) plus the boolean literals.
const RESERVED: ReadonlySet<string> = new Set([...RESERVED_KEYWORDS, "true", "false"]);

// v0.8.2: validate a rename target against the LEXER itself, so it accepts every
// real identifier — Unicode (Cyrillic/CJK), a leading digit with a letter (`2FA`),
// an internal hyphen (`arch-module`) — and nothing else (spaces, leading `_`, …).
function isValidIdentifier(name: string): boolean {
  const { tokens, errors } = tokenize(name);
  if (errors.length > 0) return false;
  const real = tokens.filter((t) => t.type !== "EOF");
  if (real.length !== 1) return false;
  const t = real[0]!;
  return (
    (t.type === "IDENTIFIER" || RESERVED_KEYWORDS.has(t.value)) &&
    t.value === name.normalize("NFC")
  );
}

export interface RenameValidationFailure {
  readonly reason: string;
}

/**
 * Pre-flight check used by editors that support `prepareProvider` —
 * returns null if the symbol can't be renamed at this cursor, or
 * the symbol's name range to highlight.
 */
export function prepareRename(
  workspace: Workspace,
  symbols: SymbolTable,
  cursor: CursorContext,
  uri: string,
  line: number,
  character: number
): { range: ReturnType<typeof toLspRange> } | null {
  const sym = resolveSymbolAtCursor(workspace, symbols, cursor, uri, line, character);
  if (!sym) return null;
  const span = sym.nameSpan ?? sym.declSpan;
  if (span) return { range: toLspRange(span) };
  // A synthesized stub (§4.13) has no declaration site — highlight the
  // identifier under the cursor (the reference rename was invoked from) so the
  // draft module/interface is still renameable.
  const hit = cursor.qualifiedNameAt(uri, line, character);
  if (hit) return { range: toLspRange(hit.span) };
  return null;
}

export function provideRename(
  workspace: Workspace,
  symbols: SymbolTable,
  cursor: CursorContext,
  refs: ReferenceIndex,
  uri: string,
  line: number,
  character: number,
  newName: string
): WorkspaceEdit | null {
  const sym = resolveSymbolAtCursor(workspace, symbols, cursor, uri, line, character);
  if (!sym) return null;

  const validationError = validateNewName(newName, sym, symbols);
  if (validationError) {
    // Surface error via thrown ResponseError-like object — the runtime
    // unwraps this; null also works in practice.
    return null;
  }

  const changes: Record<string, TextEdit[]> = {};
  const push = (u: string, edit: TextEdit) => {
    if (!changes[u]) changes[u] = [];
    // A symbol's name span can be recorded more than once — e.g. the
    // declaration plus an ancestor of a compound reference (`Orders` inside
    // `Orders.OrdersDB.Write`) both resolve to the same span. Emitting two
    // edits over identical ranges makes the editor's bulk-edit service throw
    // "Overlapping ranges" and roll back the WHOLE rename (the F2-does-nothing
    // bug). Each occurrence must be edited exactly once.
    const dup = changes[u].some(
      (e) =>
        e.range.start.line === edit.range.start.line &&
        e.range.start.character === edit.range.start.character &&
        e.range.end.line === edit.range.end.line &&
        e.range.end.character === edit.range.end.character,
    );
    if (!dup) changes[u].push(edit);
  };

  // Aspects have no single declaration — every occurrence is a recorded
  // reference, so the reference loop below rewrites them all. Editing a
  // "decl" span here would double-edit the first occurrence.
  if (sym.kind !== "aspect" && sym.definingUri && sym.nameSpan) {
    push(sym.definingUri, {
      range: toLspRange(sym.nameSpan),
      newText: newName,
    });
  }

  for (const ref of refs.referencesTo(sym.id)) {
    const file = workspace.getFile(ref.uri);
    if (!file) continue;
    const text = file.text.slice(ref.span.start.offset, ref.span.end.offset);
    const idx = text.indexOf(sym.name);
    if (idx < 0) continue;
    const startOffset = ref.span.start.offset + idx;
    const endOffset = startOffset + sym.name.length;
    push(ref.uri, {
      range: {
        start: positionAtOffset(file.text, startOffset),
        end: positionAtOffset(file.text, endOffset),
      },
      newText: newName,
    });
  }

  return { changes };
}

/**
 * Returns a failure description if the new name is invalid; null
 * otherwise.
 */
export function validateNewName(
  newName: string,
  sym: SymbolEntry,
  symbols: SymbolTable
): RenameValidationFailure | null {
  // Aspect values are string literals — any non-empty text is legal, and
  // "collision" with another aspect of the same name is the whole point
  // (same name = same aspect). Skip the identifier/reserved/collision
  // rules that govern declared symbols.
  if (sym.kind === "aspect") {
    if (newName.length === 0) return { reason: "name cannot be empty" };
    if (newName === sym.name) return { reason: "name unchanged" };
    return null;
  }
  if (!isValidIdentifier(newName)) {
    return {
      reason: `'${newName}' is not a valid identifier`,
    };
  }
  // v0.8.2 §4.9: a reserved word is a valid NAME. An interface is only ever
  // referenced as `.name` (a member position that always accepts a keyword), so
  // any keyword name is safe. Other kinds keep the guard — a module renamed to a
  // step-start keyword would break a bare caller (`select > X` reads as select).
  if (sym.kind !== "interface" && RESERVED.has(newName)) {
    return {
      reason: `'${newName}' is a reserved keyword`,
    };
  }
  if (newName === sym.name) {
    return { reason: "name unchanged" };
  }
  // Top-level symbol collision check: if renaming a module / type /
  // process / view at the workspace root, ensure no other symbol of the
  // same kind already exists with `newName` IN THE SAME SPACE — a twin in
  // a sibling space is legal (v0.13) and must not block the rename.
  if (!sym.ownerName) {
    const mySpace = symbols.spaceKeyOfUri(sym.definingUri);
    const collision = symbols
      .declaredWithName(newName)
      .find((s) => s.kind === sym.kind && symbols.spaceKeyOfUri(s.definingUri) === mySpace);
    if (collision) {
      return {
        reason: `'${newName}' is already used by another ${collision.kind}`,
      };
    }
  } else {
    // Nested-symbol collision check: same parent + same name on same kind?
    const siblings = symbols.childrenOf(sym.ownerName);
    const collision = siblings.find(
      (s) => s.kind === sym.kind && s.name === newName
    );
    if (collision) {
      return {
        reason: `'${newName}' is already used by a sibling ${collision.kind}`,
      };
    }
  }
  return null;
}

function positionAtOffset(text: string, offset: number): { line: number; character: number } {
  let line = 0;
  let character = 0;
  const end = Math.min(offset, text.length);
  for (let i = 0; i < end; i++) {
    if (text[i] === "\n") {
      line++;
      character = 0;
    } else {
      character++;
    }
  }
  return { line, character };
}
